import { useTracking } from "react-tracking";

import {
  BuiMenu,
  type IBuiMenuDividerProps,
  type IBuiMenuItemProps,
  type IBuiMenuProps,
  type IBuiSubMenuProps,
} from "@btnet/blueprint-components";

import "./BTMenu.less";

const BTMenuComponent = (props: IBuiMenuProps) => {
  return <BuiMenu {...props} />;
};

/** The click info object antd/rc-menu passes to a menu item's onClick. */
type MenuClickInfo = Parameters<NonNullable<IBuiMenuItemProps["onClick"]>>[0];

const BTMenuItemComponent = (props: IBuiMenuItemProps) => {
  const { trackEvent } = useTracking<Record<string, unknown>>({
    element: "MenuItem",
    uniqueId: props.testid,
  });

  const handleClick = (info: MenuClickInfo) => {
    props.onClick?.(info);
    trackEvent({ event: "MenuItemClick" });
  };

  return <BuiMenu.Item {...props} onClick={handleClick} />;
};

const BTSubMenuComponent = (props: IBuiSubMenuProps) => {
  return <BuiMenu.SubMenu {...props} />;
};

const BTMenuDividerComponent = (props: IBuiMenuDividerProps) => {
  return <BuiMenu.Divider {...props} />;
};

/**
 * BTMenu component - a styled menu for use as dropdown overlay content.
 * Wraps BuiMenu from @btnet/blueprint-components and fires item-click
 * analytics via the react-tracking peer dependency.
 * Intended for use with BTDropdown, which handles visibility and keyboard focus.
 * @param props - The component props.
 * @param props.testid - Test ID for the menu.
 * @param props.className - Additional CSS class names.
 * @example
 * ```tsx
 * <BTDropdown
 *   testid="actions-dropdown"
 *   overlay={
 *     <BTMenu testid="actions-menu">
 *       <BTMenu.Item testid="edit">Edit</BTMenu.Item>
 *       <BTMenu.Divider />
 *       <BTMenu.Item testid="delete">Delete</BTMenu.Item>
 *     </BTMenu>
 *   }
 * >
 *   <BdsButton testid="trigger" icon={<BdsIconDotsThree />} ariaLabel="Actions" />
 * </BTDropdown>
 * ```
 */
export const BTMenu = Object.assign(BTMenuComponent, {
  /**
   * BTMenu.Item sub-component for individual menu items.
   * Wraps BuiMenu.Item and emits a `MenuItemClick` analytics event on click.
   * @param props - The component props.
   * @param props.testid - Test ID for the menu item.
   * @param props.className - Additional CSS class names.
   * @param props.onClick - Click handler for the menu item.
   */
  Item: BTMenuItemComponent,
  /**
   * BTMenu.Divider sub-component for visual separation between menu items.
   * Wraps BuiMenu.Divider.
   */
  Divider: BTMenuDividerComponent,
  /**
   * BTMenu.SubMenu sub-component for nested menu items.
   * Wraps BuiMenu.SubMenu.
   * @param props - The component props.
   */
  SubMenu: BTSubMenuComponent,
});
